import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, RefreshCw, User as UserIcon, KeyRound, Mail, UserPlus, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PageHead, Panel, Pill, FormField, FormSection, Select, btn, field } from '../common/AdminUI';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { sendWelcomeEmail } from '../../utils/emailService';
import { hashPassword } from '../../utils/crypto';
import EmailListInput, { extractApiErrorMessage, isEmailToFetchPdfError } from '../common/EmailListInput';

const AddUserPage = ({ allUsers, onAddUser, embedded = false, onDone }) => {
  const navigate = useNavigate();
  const { allUsers: globalUsers, fetchUsers, user: currentUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailStatus, setEmailStatus] = useState(''); // Track email sending status
  
  // Define allowed roles based on current user's role
  const getAllowedRoles = () => {
    if (currentUser?.role === 'super_admin') {
      return ['admin'];
    } else if (currentUser?.role === 'admin') {
      return ['user', 'accountant', 'employee'];
    }
    return ['employee']; // Default fallback
  };

  const allowedRoles = getAllowedRoles();
  // Super admins create admin accounts, which carry the PDF-fetch email list.
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    role: allowedRoles[0] || 'employee',
    status: 'active',
    website: '',
    emailToFetchPdf: []
  });

  // Set default role when currentUser changes
  useEffect(() => {
    if (currentUser) {
      const roles = getAllowedRoles();
      setFormData(prev => ({
        ...prev,
        role: roles[0] || 'employee'
      }));
    }
  }, [currentUser]);

  const generateStrongPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
    setErrors({ ...errors, password: '' });
    setShowPassword(true);
  };

  const validateForm = () => {
    const newErrors = {};
    const usersToCheck = globalUsers.length > 0 ? globalUsers : allUsers;
    
    // Customer Name validation
    if (!formData.customerName || formData.customerName.trim().length < 2) {
      newErrors.customerName = 'Customer name must be at least 2 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      const emailExists = usersToCheck.some(u => u.email?.toLowerCase() === formData.email.toLowerCase());
      if (emailExists) {
        newErrors.email = 'This email is already registered';
      }
    }
    
    // Phone Number validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phoneNumber || !phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be exactly 10 digits';
    } else {
      const phoneExists = usersToCheck.some(u => u.mobile === formData.phoneNumber);
      if (phoneExists) {
        newErrors.phoneNumber = 'This phone number is already registered';
      }
    }
    
    // Username validation
    if (!formData.username || formData.username.trim().length < 5) {
      newErrors.username = 'Username must be at least 5 characters';
    } else {
      const usernameExists = usersToCheck.some(u => u.username?.toLowerCase() === formData.username.toLowerCase());
      if (usernameExists) {
        newErrors.username = 'This username is already taken';
      }
    }
    
    // Password validation
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Website validation (optional but if provided must be valid)
    if (formData.website && formData.website.trim()) {
      const urlRegex = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlRegex.test(formData.website)) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    if (isSuperAdmin && formData.emailToFetchPdf.length === 0) {
      newErrors.emailToFetchPdf = 'Add at least one email';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setApiError('');
    setSuccessMessage('');
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      const hashedPwd = await hashPassword(formData.password);
      const response = await apiCall(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          Name: formData.customerName,
          phNum: `+91${formData.phoneNumber}`,
          role: formData.role,
          status: formData.status,
          password: hashedPwd,
          website: formData.website || '',
          created_by: currentUser?.id || null,
          ...(isSuperAdmin && { emailToFetchPdf: formData.emailToFetchPdf })
        })
      });

      const data = await response.json().catch(() => ({}));
      console.log('Register response:', response.status, data);

      // The API can report a failure with a 2xx body (success:false), so
      // don't trust the status code alone.
      const failed = !response.ok || data?.success === false || data?.status === 'error';

      if (!failed) {
        // User created successfully
        setSuccessMessage(data.message || 'User registered successfully');
        
        // Send welcome email ONLY to users with 'user' role
        if (formData.role === 'user') {
          setEmailStatus('Sending welcome email...');
          const emailResult = await sendWelcomeEmail({
            name: formData.customerName,
            email: formData.email,
            username: formData.username,
            password: formData.password
          });
          
          if (emailResult.success) {
            setEmailStatus('✅ Welcome email sent successfully!');
          } else {
            setEmailStatus(`⚠️ Email failed: ${emailResult.error || 'Unknown error'}`);
            console.error('Email send failed:', emailResult.error);
          }
        }
        
        // Fetch fresh data from API
        await fetchUsers();
        
        // Redirect after 2 seconds
        setTimeout(() => {
          if (embedded) onDone?.();
          else navigate('/admin', { replace: true });
        }, 2000);
      } else {
        const message = extractApiErrorMessage(data, 'Failed to create user. Please try again.');
        if (isSuperAdmin && isEmailToFetchPdfError(data, message, formData.emailToFetchPdf)) {
          setErrors((prev) => ({ ...prev, emailToFetchPdf: message }));
        } else {
          setApiError(message);
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      setApiError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const roleLabel = (role) => (role === 'super_admin' ? 'Super Admin' : role.charAt(0).toUpperCase() + role.slice(1));

  const leave = () => {
    if (embedded) onDone?.();
    else navigate(isSuperAdmin ? '/super-admin' : '/admin');
  };

  const body = (
    <>
      {!embedded && (
        <button
          type="button"
          onClick={leave}
          className="inline-flex items-center gap-2 mb-4 text-[13px] font-bold text-slate-500 hover:text-slate-800
                     dark:text-slate-400 dark:hover:text-slate-100 transition"
        >
          <ArrowLeft size={16} /> Back to console
        </button>
      )}

        <PageHead
          eyebrow={isSuperAdmin ? 'Super admin core · Directory' : 'Admin · Directory'}
          title="Add New User"
          badge={<Pill tone={isSuperAdmin ? 'violet' : 'blue'}>{allowedRoles.map(roleLabel).join(' / ')}</Pill>}
          subtitle={isSuperAdmin
            ? 'Admin accounts carry the inbox the LEO automation reads its PDFs from.'
            : 'Client, accountant and employee accounts under your admin.'}
        />

        <form onSubmit={handleSubmit} autoComplete="off">
          <Panel className="px-5 sm:px-6 py-2">
            <FormSection
              title="Identity"
              description="Who the account belongs to."
              icon={UserIcon}
              tone="blue"
            >
              <FormField label="Name" required error={errors.customerName}>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  autoComplete="off"
                  placeholder="Enter full name"
                  className={`${field} ${errors.customerName ? 'border-rose-400' : ''}`}
                />
              </FormField>

              <FormField label="Email" required error={errors.email}>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  autoComplete="off"
                  placeholder="user@example.com"
                  className={`${field} ${errors.email ? 'border-rose-400' : ''}`}
                />
              </FormField>

              <FormField label="Phone number" required error={errors.phoneNumber}>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-bold text-slate-400 pointer-events-none">+91</span>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    autoComplete="off"
                    placeholder="9876543210"
                    className={`${field} pl-12 ${errors.phoneNumber ? 'border-rose-400' : ''}`}
                  />
                </div>
              </FormField>

              <FormField label="Website" hint="(optional)" error={errors.website}>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  autoComplete="off"
                  placeholder="https://example.com"
                  className={`${field} ${errors.website ? 'border-rose-400' : ''}`}
                />
              </FormField>
            </FormSection>

            <FormSection
              title="Access"
              description="What they sign in with, and what they may reach."
              icon={KeyRound}
              tone="amber"
            >
              <FormField label="Username" required error={errors.username}>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  autoComplete="off"
                  placeholder="Unique username"
                  className={`${field} ${errors.username ? 'border-rose-400' : ''}`}
                />
              </FormField>

              <FormField
                label="Password"
                required
                error={errors.password}
              >
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    autoComplete="new-password"
                    placeholder="Min 6 characters"
                    className={`${field} pr-20 ${errors.password ? 'border-rose-400' : ''}`}
                  />
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex gap-0.5">
                    <button type="button" onClick={generateStrongPassword} title="Generate a strong password"
                      className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100
                                 dark:hover:text-slate-100 dark:hover:bg-slate-700 transition">
                      <RefreshCw size={16} />
                    </button>
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100
                                 dark:hover:text-slate-100 dark:hover:bg-slate-700 transition">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {!errors.password && (
                  <p className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400">
                    <button type="button" onClick={generateStrongPassword}
                      className="font-bold text-blue-600 dark:text-blue-400 hover:underline">
                      Generate a strong one
                    </button>
                    {' '}— share it with the user, it is not shown again.
                  </p>
                )}
              </FormField>

              <FormField label="Role" required>
                {allowedRoles.length === 1 ? (
                  <div className={`${field} flex items-center justify-between cursor-default`}>
                    <span className="font-bold">{roleLabel(allowedRoles[0])}</span>
                    <Pill tone="slate">only role you may create</Pill>
                  </div>
                ) : (
                  <Select
                    clearable={false}
                    value={formData.role}
                    onChange={(v) => handleInputChange('role', v || allowedRoles[0])}
                    options={allowedRoles.map((r) => ({ value: r, label: roleLabel(r) }))}
                  />
                )}
              </FormField>

              <FormField label="Status" required>
                <Select
                  clearable={false}
                  value={formData.status}
                  onChange={(v) => handleInputChange('status', v || 'active')}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                />
              </FormField>
            </FormSection>

            {isSuperAdmin && (
              <FormSection
                title="Document inbox"
                description="The mailbox this admin's LEO PDFs are fetched from."
                icon={Mail}
                tone="emerald"
              >
                <div className="md:col-span-2">
                  <EmailListInput
                    value={formData.emailToFetchPdf}
                    onChange={(emails) => handleInputChange('emailToFetchPdf', emails)}
                    error={errors.emailToFetchPdf}
                  />
                </div>
              </FormSection>
            )}
          </Panel>

          {successMessage && (
            <Panel className="mt-4 px-5 py-4 border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13.5px] font-bold text-emerald-800 dark:text-emerald-300">{successMessage}</p>
                  {emailStatus && <p className="text-[12px] text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">{emailStatus}</p>}
                </div>
              </div>
            </Panel>
          )}

          {apiError && (
            <Panel className="mt-4 px-5 py-4 border-rose-200 bg-rose-50/70 dark:border-rose-500/30 dark:bg-rose-500/10">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                <p className="text-[13.5px] font-semibold text-rose-700 dark:text-rose-300">{apiError}</p>
              </div>
            </Panel>
          )}

          <div className="mt-5 flex flex-col-reverse sm:flex-row gap-3">
            <button type="button" disabled={loading} className={`${btn.quiet} justify-center disabled:opacity-50`}
              onClick={leave}>
              Cancel
            </button>
            <button type="submit" disabled={loading} className={`${btn.primary} flex-1 justify-center disabled:opacity-60`}>
              {loading ? <><RefreshCw size={16} className="animate-spin" /> Creating user…</> : <><UserPlus size={16} /> Create user</>}
            </button>
          </div>
      </form>
    </>
  );

  // Inside the console the shell already paints the page and the sidebar says
  // where you are; on its own route the page still has to do both itself.
  return embedded ? <div className="max-w-5xl">{body}</div> : (
    <div className="min-h-screen bg-[#f4f7fc] dark:bg-[#080d19]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">{body}</div>
    </div>
  );
};

export default AddUserPage;
